(function () {

    'use strict';

    let lib;
    let metricFn;
    let _;

    if (typeof window === 'undefined') {

        module.exports = wrapper;

        _ = require('lodash');
        lib = {
            format: require('./format.js'),
            timePeriod: require('./timePeriod.js')
        };

        metricFn = exports(
            require('../../enums/metrics.js'),
            _
        );

    } else {

        RivalIQ.metric = lib = wrapper;

        _ = window._;
        metricFn = exports(
            RivalIQ.enums,
            _
        );
    }

    // This is the main entry point to the static metric functions. Keeping
    // these static has been pretty simplifying for the front and back end,
    // especially in testability and ensuring that all metric state is encoded
    // in the plain javascript object definition

    _.extend(wrapper, metricFn);

    // This wrapper is used to help handle some load order issues. Ideally we
    // wont need it, though auto-generating something like this could make use
    // easier. I think of it like the lodash wrapper
    function wrapper(metric) {

        const prototype = {
            defaultFormat: function (data, options) {
                const value = (_.isArray(data) ? _.last(data) : data);
                return lib.format.formatFn(this, data, options)(value, options);
            },
            getPeriod: function () {
                return lib.timePeriod.getPeriod(this);
            },
            getDataTimePeriod: function () {
                return lib.timePeriod.getDataTimePeriod(this);
            },
            isMainPeriod: function (data) {
                return lib.timePeriod.isMainPeriod(this, data);
            },
            isNotRelative: function (data) {
                return lib.timePeriod.isNotRelative(this, data);
            },
            getMetricType: function () {
                return lib.property.metricType(this);
            }
        };

        return _.create(prototype, _.extend({}, metric));
    }

    function exports(metricEnums, _) {
        const fn = {};

        // This function sets the prototype of the bottom member of a prototype
        // chain. Used to allow metric.build to function in the expected way
        function setBasePrototype(parent, child) {
            // Rebuild the prototype chain to put parent at the bottom
            const stack = [];

            // NOTE this is a special case for older metrics that used the
            // string syntax instead of requiring an object
            if (_.isString(child)) {
                child = {
                    property: child
                };
            }

            let prototype = Object.getPrototypeOf(child);
            while (prototype instanceof Object) {
                if (parent === prototype) {
                    return child;
                }

                stack.unshift(prototype);
                prototype = Object.getPrototypeOf(prototype);
            }
            stack.unshift(parent);
            stack.push(child);

            while (stack.length > 1) {
                stack.unshift(_.create(stack.shift(), stack.shift()));
            }

            return stack[0];
        }

        /*
         * Returns a new definition built from the parent definition
         * Dimensions are additive unless clobberDimensions is true
         */
        fn.build = function (parent, child, options) {
            options = options || {};

            if (_.isArray(child)) {
                return _.map(child, _.partial(fn.build, parent, _, options));
            }

            let dimensions = child.dimensions;
            if (!options.clobberDimensions) {
                if (options.prependDimensions) {
                    dimensions = _.union(child.dimensions || [], parent.dimensions);
                } else {
                    dimensions = _.union((parent || {}).dimensions || [], child.dimensions);
                }
            }

            const additional = {
                dimensions: dimensions
            };

            const metric = setBasePrototype(parent, child);

            return _.create(metric, additional);
        };

        // Subtract subtraction from metric. This returns a copy with removed
        // dimensions and dimensionValues from the metric
        fn.subtract = function (metric, subtraction) {
            const mask = {};
            const omit = _.clone(_.get(subtraction, ['dimensions'], []));

            const dimensions = _.without.apply(null, [metric.dimensions].concat(omit));
            if (!dimensions.length) {
                omit.push('dimensions');
            } else {
                mask.dimensions = dimensions;
            }

            return _.extend(_.omit(metric, omit), mask);
        };

        // Finds the index of metric in metrics. This can use the fuzzy: true
        // option to find metrics that match on property but not dimension
        fn.findIndex = function (metrics, metric, options) {
            return _.findIndex(metrics, function (m) {
                return fn.equal(metric, m, options);
            });
        };

        // Returns true if the metric has the given dimension
        fn.hasDimension = function (metric, dimension) {
            return _.indexOf(metric.dimensions, dimension) !== -1;
        };

        // Returns a new metric with dimensions cut off the end until the free
        // dimension count === count
        // This has been suprisingly useful for building charts from arbitrary
        // metrics. However, in the future it is probably better to design
        // widgets that take metrics w/ 0 free dimensions and build up the free
        // dimensions from their own props
        fn.pin = function (metric, count) {
            if (_.isArray(metric)) {
                return _.map(metric, _.partial(fn.pin, _, count));
            }

            const freeDimensions = fn.getFreeDimensions(metric);
            const toRemove = freeDimensions.slice(count, freeDimensions.length);

            const dimensions = _.without.apply(undefined, [metric.dimensions].concat(toRemove));
            return _.create(metric, {
                dimensions: dimensions
            });
        };

        /*
         * Gets the list of dimensions whose values have not been defined
         */
        fn.getFreeDimensions = function (metric) {
            return _.reject(metric.dimensions, function (dimension) {
                return !_.isUndefined(metric[dimension]);
            });
        };

        /*
         * Returns a new metric with the passed dimensions freed
         */
        fn.freeDimensions = function (metric, dimensions) {
            if (_.isArray(metric)) {
                return _.map(metric, _.partial(fn.freeDimensions, _, dimensions));
            }

            const overrides = {};
            _.each(dimensions, function (dimension) {
                overrides[dimension] = undefined;
            });

            return _.create(metric, overrides);
        };

        /*
         * Given a metric and dimension return the value of that dimension
         */
        fn.dimensionValue = function (metric, dimension, data) {
            if (!_.isUndefined(metric[dimension])) {
                return metric[dimension];
            }

            if (!data) {
                return;
            }

            const idx = _.indexOf(fn.getFreeDimensions(metric), dimension);
            return data[idx];
        };

        fn.freeDimensionIndex = function (metric, dimension) {
            return _.indexOf(fn.getFreeDimensions(metric), dimension);
        };

        /*
         * Given an array of data return all unique dimension values for the
         * given dimension
         */
        fn.dimensionValues = function (metric, dimension, data) {
            if (!_.isUndefined(metric[dimension])) {
                return [metric[dimension]];
            }

            const idx = fn.freeDimensionIndex(metric, dimension);
            return _.reject(_.uniq(_.map(data, function (datum) {
                return datum[idx];
            })), _.isUndefined);
        };

        /*
         * Assigns data array to its corresponding dimension thereby
         * fixing free metric dimensions for property lookup
         */
        fn.assign = function (metric, data) {
            if (!data) {
                return metric;
            }

            const child = {};
            _.each(fn.getFreeDimensions(metric), function (dimension, i) {
                child[dimension] = data[i];
            });

            return fn.build(metric, child);
        };

        fn.equal = function (metricA, metricB, options) {
            options = options || {};

            if (!options.fuzzy) {
                return _.isEqualWith(metricA, metricB, prototypeComparison);
            }

            // A very naive fuzzy equality
            if (metricA.property === metricB.property) {
                return true;
            }

            return false;
        };

        function prototypeComparison(a, b) {
            if (a && a.constructor === Array && b && b.constructor === Array) {
                if (a.length !== b.length) {
                    return false;
                }
                let i = 0;
                while (i < a.length) {
                    if (!prototypeComparison(a[i], b[i])) {
                        return false;
                    }
                    i++;
                }

                return true;
            }

            if (a && a.constructor === Object && b && b.constructor === Object) {
                return _.isEqual(_.extend({}, a), _.extend({}, b));
            }
        }

        return fn;
    }
})();
